import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { ApiService } from '../api.service';
import { FrontendLoggerService } from '../frontend-logger.service';
import { TokenService } from '../token.service';
import { AuthFlowStateService, PendingInviteContext, ResolvedAuthFlow } from './auth-flow-state.service';

export type AuthFlowType = ResolvedAuthFlow;

export interface AuthFlowResolutionRequest {
  requestedTarget: string;
  pendingInviteToken?: string | null;
  pendingInviteEmail?: string | null;
}

export interface AuthFlowResolutionResponse {
  flow: AuthFlowType;
  target?: string | null;
  inviteToken?: string | null;
  inviteEmail?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthFlowOrchestratorService {
  private readonly endpoint = 'auth/flow-orchestrator/resolve';

  constructor(
    private readonly apiService: ApiService,
    private readonly tokenService: TokenService,
    private readonly authFlowState: AuthFlowStateService,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  resolvePostAuthTarget(defaultTarget: string): Observable<string> {
    const pendingInvite = this.authFlowState.getPendingInviteContext();

    return this.tokenService.getToken().pipe(
      take(1),
      switchMap((token) => {
        if (!token || !token.trim()) {
          this.frontendLogger.warn('[AuthFlowOrchestrator] No access token available. Falling back to legacy routing.');
          return of(this.getLegacyTarget(defaultTarget));
        }

        const body: AuthFlowResolutionRequest = {
          requestedTarget: defaultTarget,
          pendingInviteToken: pendingInvite?.token ?? null,
          pendingInviteEmail: pendingInvite?.email ?? null
        };

        return this.apiService.post<AuthFlowResolutionResponse>(this.endpoint, body).pipe(
          map((response) => this.mapResponseToTarget(response, defaultTarget, pendingInvite)),
          catchError((error) => {
            const message = error?.status === 404
              ? '[AuthFlowOrchestrator] Backend orchestrator endpoint not available. Falling back to legacy routing.'
              : '[AuthFlowOrchestrator] Error resolving post-auth flow. Falling back to legacy routing.';
            const detail = error?.message || JSON.stringify(error);
            if (error?.status === 404) {
              this.frontendLogger.warn(message, detail);
            } else {
              this.frontendLogger.error(message, detail);
            }
            return of(this.getLegacyTarget(defaultTarget));
          })
        );
      })
    );
  }

  private mapResponseToTarget(
    response: AuthFlowResolutionResponse | null | undefined,
    defaultTarget: string,
    pendingInvite: PendingInviteContext | null
  ): string {
    const flow = response?.flow;
    const explicitTarget = (response?.target || '').trim();

    if (flow === 'registered') {
      this.authFlowState.setResolvedFlow('registered');
      this.authFlowState.clearPendingInviteContext();
      return explicitTarget || defaultTarget;
    }

    if (flow === 'new_user') {
      this.authFlowState.setResolvedFlow('new_user');
      this.authFlowState.clearPendingInviteContext();
      return explicitTarget || '/wizard';
    }

    if (flow === 'invited') {
      const inviteToken = (response?.inviteToken || pendingInvite?.token || '').trim();
      const inviteEmail = (response?.inviteEmail || pendingInvite?.email || '').trim().toLowerCase();

      if (inviteToken && inviteEmail) {
        this.authFlowState.setResolvedFlow('invited');
        this.authFlowState.setPendingInviteContext(inviteToken, inviteEmail);
        if (explicitTarget && explicitTarget.split('?')[0].trim() !== '/onboarding') {
          this.frontendLogger.warn('[AuthFlowOrchestrator] Invited flow returned unexpected target. Forcing /onboarding.', explicitTarget);
        }

        return '/onboarding';
      }

      this.frontendLogger.warn('[AuthFlowOrchestrator] Invited flow returned without invite context. Falling back to legacy routing.');
      return this.getLegacyTarget(defaultTarget);
    }

    this.frontendLogger.warn('[AuthFlowOrchestrator] Unknown flow returned by backend. Falling back to legacy routing.', JSON.stringify(response));
    return this.getLegacyTarget(defaultTarget);
  }

  private getLegacyTarget(defaultTarget: string): string {
    return this.authFlowState.getPendingInviteTarget() || defaultTarget;
  }
}